import { Component, signal, ChangeDetectionStrategy } from '@angular/core';
import { AppCommonModule } from 'src/app/common.module';
import { SegmentButton, SegmentListComponent } from 'src/app/components/segment-list/segment-list.component';
import { DashboardViewComponent } from 'src/app/components/views/dashboard/view-dashboard.component';
import { ActionRowComponent } from 'src/app/ui/action-row/action-row.component';
import { CardComponent } from 'src/app/ui/card/card.component';
import { DataBlockComponent } from 'src/app/ui/data-block/data-block.component';
import { DataSurfaceComponent } from 'src/app/ui/data-surface/data-surface.component';
import { FormFieldComponent } from 'src/app/ui/form-field/form-field.component';
import { FormStackComponent } from 'src/app/ui/form-stack/form-stack.component';
import { ListItemComponent } from 'src/app/ui/list-item/list-item.component';
import { MetricCardItem, MetricCardsComponent } from 'src/app/ui/metric-cards/metric-cards.component';
import { ModalShellComponent, ModalShellWorkspaceLayout } from 'src/app/ui/modal-shell/modal-shell.component';
import { PageHeaderActionsComponent } from 'src/app/ui/page-header-actions/page-header-actions.component';
import { SectionHeaderComponent } from 'src/app/ui/section-header/section-header.component';
import { StatusChipComponent } from 'src/app/ui/status-chip/status-chip.component';

@Component({
  selector: 'app-ui-showroom-page',
  templateUrl: './ui-showroom.page.html',
  styleUrls: ['./ui-showroom.page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    DashboardViewComponent,
    SegmentListComponent,
    DataSurfaceComponent,
    DataBlockComponent,
    ModalShellComponent,
    PageHeaderActionsComponent,
    CardComponent,
    SectionHeaderComponent,
    FormFieldComponent,
    FormStackComponent,
    ListItemComponent,
    MetricCardsComponent,
    StatusChipComponent,
    ActionRowComponent,
  ],
})
export class UiShowroomPage {
  readonly smallModalCssClass = ['modal-default', 'stack-modal'];
  readonly workspaceModalCssClass = ['workspace-modal', 'stack-modal'];

  readonly smallModalOpen = signal(false);
  readonly workspaceModalOpen = signal(false);
  readonly workspaceLayout = signal<ModalShellWorkspaceLayout>('single');
  readonly sectionTabsValue = signal('overview');
  readonly mobileTabsValue = signal('overview');
  readonly sectionTabsButtons: SegmentButton[] = [
    { value: 'overview', name: 'Overview', show: true },
    { value: 'details', name: 'Details', show: true },
    { value: 'history', name: 'History', show: true },
    { value: 'settings', name: 'Settings', show: true },
  ];

  readonly mobileTabsButtons: SegmentButton[] = [
    { value: 'overview', name: 'Overview with long label', show: true },
    { value: 'work-items', name: 'Work items and timeline status', show: true },
    { value: 'dependencies', name: 'Dependencies and linked tasks', show: true },
    { value: 'communication', name: 'Communication and stakeholder notes', show: true },
  ];

  readonly pageListItems = [
    {
      title: 'Building A - Weekly review',
      subtitle: 'Updated today',
      trailing: 'Open',
      secondary: 'Owner: Site lead',
    },
    {
      title: 'Equipment checklist',
      subtitle: '5 items pending',
      trailing: 'Draft',
      secondary: 'Next due: tomorrow',
    },
  ];

  readonly modalListItems = [
    {
      title: 'Template A',
      subtitle: 'Ready to use',
      trailing: 'Active',
    },
    {
      title: 'Template B',
      subtitle: 'Requires updates',
      trailing: 'Pending',
    },
  ];

  readonly asideListItems = [
    {
      title: 'Owner',
      trailing: 'UI Team',
      secondary: 'Workspace level',
    },
    {
      title: 'Status',
      trailing: 'Draft',
      secondary: 'Not published',
    },
  ];

  readonly metricCards: MetricCardItem[] = [
    { key: 'open', label: 'Open items', labelKey: 'Open items', value: 24, detail: 'Across active workspaces' },
    { key: 'completed', label: 'Completed', labelKey: 'Completed', value: 132, detail: 'Last 30 days', tone: 'success' },
    { key: 'at-risk', label: 'At risk', labelKey: 'At risk', value: 7, detail: 'Needs follow-up', tone: 'warning' },
    { key: 'blocked', label: 'Blocked', labelKey: 'Blocked', value: 3, detail: 'Escalation needed', tone: 'danger' },
  ];

  readonly compactMetricCards: MetricCardItem[] = [
    { key: 'today', label: 'Today', labelKey: 'Today', value: '12' },
    { key: 'week', label: 'This week', labelKey: 'This week', value: '48', tone: 'neutral' },
    { key: 'delta', label: 'Delta', labelKey: 'Delta', value: '+9%', detail: 'vs previous week', tone: 'success' },
  ];

  readonly mobileMetricCards: MetricCardItem[] = [
    {
      key: 'mobile-overview',
      label: 'Mobile overview with long title',
      labelKey: 'Mobile overview with long title',
      value: '145',
      detail: 'Scroll row item',
    },
    {
      key: 'mobile-sla',
      label: 'Service level and completion trend',
      labelKey: 'Service level and completion trend',
      value: '94%',
      detail: 'Last 14 days',
      tone: 'success',
    },
    {
      key: 'mobile-risk',
      label: 'Risk and escalation count',
      labelKey: 'Risk and escalation count',
      value: '11',
      detail: 'Needs attention',
      tone: 'warning',
    },
  ];

  openSmallModal(): void {
    this.smallModalOpen.set(true);
  }

  onSectionTabsChanged(value: string): void {
    this.sectionTabsValue.set(value);
  }

  onMobileTabsChanged(value: string): void {
    this.mobileTabsValue.set(value);
  }

  closeSmallModal(): void {
    this.smallModalOpen.set(false);
  }

  openWorkspaceModal(layout: ModalShellWorkspaceLayout): void {
    this.workspaceLayout.set(layout);
    this.workspaceModalOpen.set(true);
  }

  closeWorkspaceModal(): void {
    this.workspaceModalOpen.set(false);
  }

  workspaceLayoutLabel(): string {
    const layout = this.workspaceLayout();
    if (layout === 'two-column') {
      return 'two-column';
    }

    if (layout === 'two-column-reverse') {
      return 'two-column-reverse';
    }

    return 'single';
  }
}
